import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import * as PropTypes from 'prop-types';

import { StudySiteApi } from '../../../../../../api';
import Select from '../../../../../../common/data-entry/Select';
import { CLOSED, TREATMENT } from '../../../../../../constants/ssuStatuses';
import { ROLE_SYSTEM_ADMINISTRATOR } from '../../../../../../constants/userRoles';
import { i18nt } from '../../../../../../root';
import { userHasRole } from '../../../../../../services/auth';
import { NSPP_PATIENT } from '../../../../../../services/i18n/namespaces';
import { caseInsensitiveStringIncludes } from '../../../../../../services/string';
import { SSUFilter } from '../../../../../SSUFilter/SSUFilter';
import { SSUFilterMaterialRepresentation } from '../../../../../SSUFilter/SSUFilterMaterialRepresentation';
import { INITIAL_PATIENT_STATUSES } from '../../constants';

export default function StudySiteStatusSourceSelect(props) {
  const { t } = useTranslation();
  const {
    ssuProvider,
    ssuIdProvider,
    onSSUFilterChange,
    getValidationMessage,
    sources,
    setSource,
    source,
    setStatus,
    status,
    className,
    studiesToReject
  } = props;

  const selectedSSuProvider = useMemo(() => (ssuProvider ? ssuProvider : defaultSsuProvider), [ssuProvider]);
  const handleSsuProviderUpdate = useMemo(() => !!ssuProvider, [ssuProvider]);

  return (
    <div className={cx('eds-study-site-status-source-select', className)}>
      <div className="ssss-study-site">
        <SSUFilter
          handleSSUFilterChange={onSSUFilterChange}
          studiesToReject={studiesToReject}
          ssuProvider={selectedSSuProvider}
          ssuIdProvider={ssuIdProvider}
          handleSsuProviderUpdate={handleSsuProviderUpdate}
          data-testid="ssu-filter"
        >
          <SSUFilterMaterialRepresentation
            isStudyRequired={!getValidationMessage('study')}
            isSiteRequired={!getValidationMessage('site')}
            studyValidationMessage={getValidationMessage('study')}
            siteValidationMessage={getValidationMessage('site')}
          />
        </SSUFilter>
      </div>
      <div className="ssss-status-source">
        <Select
          label={t(`${NSPP_PATIENT}:info.studies.source`)}
          clearSearchOnSelection={false}
          searchable
          clearable={false}
          closeOnSelectedOptionClick={false}
          deselectOnSelectedOptionClick={false}
          optionLabelKey={'source'}
          onChange={setSource}
          dataSource={sources}
          valid={!getValidationMessage('source')}
          validationMessage={getValidationMessage('source')}
          value={source || null}
          required
          className="source-select"
        />
        <Select
          label={t(`${NSPP_PATIENT}:status`)}
          clearSearchOnSelection={false}
          searchable
          clearable={false}
          closeOnSelectedOptionClick={false}
          deselectOnSelectedOptionClick={false}
          onChange={setStatus}
          dataSource={INITIAL_PATIENT_STATUSES}
          value={status || null}
          valid={!getValidationMessage('status')}
          validationMessage={getValidationMessage('status')}
          required
          customOptionTemplateFunction={customOptionTemplateFunction}
          customSelectedValueTemplateFunction={customSelectedValueTemplateFunction}
          customFilterFunction={customFilterFunction}
        />
      </div>
    </div>
  );
}

function defaultSsuProvider() {
  return StudySiteApi.getAllStudySitesAndMap().then(({ data: studySites }) => {
    return studySites.filter(
      ({ siteStatus }) => userHasRole(ROLE_SYSTEM_ADMINISTRATOR) || ![TREATMENT, CLOSED].includes(siteStatus)
    );
  });
}

function customOptionTemplateFunction(option) {
  return i18nt(`statuses.${option.name}`, { ns: NSPP_PATIENT });
}

function customSelectedValueTemplateFunction(options) {
  if (!options[0]) {
    return null;
  }
  return customOptionTemplateFunction(options[0]);
}

function customFilterFunction(option, index, collection, searchString) {
  return caseInsensitiveStringIncludes(i18nt(`statuses.${option.name}`, { ns: NSPP_PATIENT }), searchString);
}

StudySiteStatusSourceSelect.propTypes = {
  ssuProvider: PropTypes.func,
  ssuIdProvider: PropTypes.func,
  onSSUFilterChange: PropTypes.func,
  getValidationMessage: PropTypes.func,
  sources: PropTypes.array,
  setSource: PropTypes.func,
  source: PropTypes.object,
  setStatus: PropTypes.func,
  status: PropTypes.object,
  className: PropTypes.string,
  studyToReject: PropTypes.array
};
